import type { spades } from "@app/rules";
import { SUITS } from "@app/rules";
import { useT } from "../i18n/context";
import type { BiddingView } from "../variants/types";
import { send } from "../ws/singleton";
import { SUIT_GLYPH } from "./Card";
import { Key, OptionTile, Sheet } from "./Sheet";

const ALL_BIDS = [5, 6, 7, 8, 9, 10, 11, 12, 13];

const bid = (amount: number | "pass") => send({ type: "move", move: { kind: "bid", amount } });

/** Bidding (design 4): every amount stays in place; only what the server allows is enabled (review 0004 S7). */
export function BidSheet({
  view,
  names,
  narrow = false,
}: {
  view: BiddingView;
  names: (string | null)[];
  /** Portrait: two rows of keys. */
  narrow?: boolean;
}) {
  const { t } = useT();
  const bids = view.validBids;
  if (bids === null) return null;
  const highest = view.bidding?.highest ?? null;
  // Tarneeb opens at seven; the keys below the floor would only ever be disabled.
  const keys = ALL_BIDS.filter((amount) => amount >= (view.variant === "tarneeb" ? 7 : 5));
  const aside = highest
    ? t("table.bid.canSay", {
        name: names[highest.seat] ?? "?",
        amount: highest.amount,
        min: bids.min,
      })
    : t("table.bid.open", { min: bids.min });
  return (
    <Sheet title={t("table.bid.title")} aside={aside} labelId="bid-title" dense={narrow}>
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(${narrow ? 5 : keys.length}, minmax(0, 1fr))` }}
      >
        {keys.map((amount) => (
          <Key
            key={amount}
            label={String(amount)}
            dense={narrow}
            disabled={amount < bids.min || amount > bids.max}
            onClick={() => bid(amount)}
          />
        ))}
      </div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[12px] text-ink/55">
          {view.variant === "batak" ? t("table.bid.note") : null}
        </span>
        {bids.canPass ? (
          <Key
            label={t("table.bid.pass")}
            tone="pass"
            dense
            className="flex-none rounded-pill px-7 text-[14px]"
            onClick={() => bid("pass")}
          />
        ) : null}
      </div>
    </Sheet>
  );
}

/** Trump choice (design 5): four keys, each with how many of that suit you hold. */
export function TrumpSheet({ view, dense = false }: { view: BiddingView; dense?: boolean }) {
  const { t } = useT();
  const amount = view.contract?.amount ?? 0;
  return (
    <Sheet
      title={t("table.trump.title")}
      aside={t("table.trump.forHand", { amount })}
      labelId="trump-title"
      dense={dense}
    >
      <div className="grid grid-cols-4 gap-2.5">
        {SUITS.map((suit) => {
          const count = view.yourHand.filter((c) => c.suit === suit).length;
          const red = suit === "hearts" || suit === "diamonds";
          return (
            <OptionTile
              key={suit}
              glyph={<span className={red ? "text-suit-red" : "text-ink"}>{SUIT_GLYPH[suit]}</span>}
              hint={t("table.trump.inHand", { count })}
              name={`${t(`suit.${suit}`)}, ${t("table.trump.inHand", { count })}`}
              dense={dense}
              onClick={() => send({ type: "move", move: { kind: "trump", suit } })}
            />
          );
        })}
      </div>
    </Sheet>
  );
}

/** Spades: every seat bids once, 1-13 or Nil (review 0004 S5). */
export function SpadesBidSheet({
  view,
  narrow = false,
}: {
  view: spades.SpadesView;
  narrow?: boolean;
}) {
  const { t } = useT();
  const partnerBid = view.bids[view.partner] ?? null;
  const aside = partnerBid === null ? null : t("spades.bid.aside", { bid: partnerBid });
  return (
    <Sheet title={t("spades.bid.title")} aside={aside} labelId="bid-title" dense={narrow}>
      <div className={`grid gap-2 ${narrow ? "grid-cols-5" : "grid-cols-7"}`}>
        {Array.from({ length: 13 }, (_, i) => i + 1).map((amount) => (
          <Key key={amount} label={String(amount)} dense={narrow} onClick={() => bid(amount)} />
        ))}
        <Key label={t("spades.bid.nil")} tone="gold" dense={narrow} onClick={() => bid(0)} />
      </div>
    </Sheet>
  );
}
